import { useState, useEffect } from 'react';
import { goApi } from '../services';
import { apiRoutes } from '../services/apiRoutes';

interface QuestionPaper {
    id: string;
    name: string;
    content: string;
    type: string;
    [key: string]: any;
}

interface UseQuestionPaperReturn {
    questionPaper: QuestionPaper[];
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

const questionPaperCache: Record<string, QuestionPaper[]> = {};

const getCacheKey = (testId: string, sectionId?: string, languageCode?: string) =>
    `${testId}::${sectionId || ''}::${languageCode || ''}`;

export const useQuestionPaper = (
    testId: string,
    sectionId?: string,
    languageCode?: string,
): UseQuestionPaperReturn => {
    const cacheKey = getCacheKey(testId, sectionId, languageCode);
    const [questionPaper, setQuestionPaper] = useState<QuestionPaper[]>(
        () => questionPaperCache[cacheKey] || [],
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchQuestionPaper = async (forceRefresh = false) => {
        if (!testId) {
            setQuestionPaper([]);
            return;
        }

        const cached = questionPaperCache[cacheKey];
        if (cached && !forceRefresh) {
            setQuestionPaper(cached);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await goApi.get(
                apiRoutes.QUESTION_PAPER(testId, sectionId, languageCode),
            );

            if (response?.data) {
                questionPaperCache[cacheKey] = response.data;
                setQuestionPaper(response.data);
            } else {
                setQuestionPaper([]);
            }
        } catch (err: any) {
            setError(err?.message || 'Failed to fetch question papers');
            setQuestionPaper([]);
        } finally {
            setLoading(false);
        }
    };

    const refetch = async () => {
        delete questionPaperCache[cacheKey];
        await fetchQuestionPaper(true);
    };

    useEffect(() => {
        fetchQuestionPaper();
    }, [testId, sectionId, languageCode]);

    return { questionPaper, loading, error, refetch };
};

export default useQuestionPaper;
